import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from "../../feature/banner/bannerThunks";
import {
  selectBannerList,
  selectBannerLoading,
  selectBannerUpdateLoading,
} from "../../feature/banner/bannerSelector";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Typography,
  Pagination,
  Stack,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Switch,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams } from "react-router-dom";
import Loader from "../../components/commonComponents/Loader";
import CommonButton from "../../components/commonComponents/CommonButton";
import CommonLabel from "../../components/commonComponents/CommonLabel";
import CommonToast from "../../components/commonComponents/Toster";
import CommonSearchField from "../../components/commonComponents/CommonSearchField";

const Banner = () => {
  const dispatch = useDispatch();
  const banners = useSelector(selectBannerList);
  const loading = useSelector(selectBannerLoading);
  const updateLoading = useSelector(selectBannerUpdateLoading);
  const { id } = useParams();

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [imageLoaded, setImageLoaded] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const [isViewing, setIsViewing] = useState(false);
  const [viewBanner, setViewBanner] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  const [form, setForm] = useState({
    title: "",
    image: null,
    CTA_text: "",
    CTA_link: "",
    order: 1,
    status: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(getBanners({ title: searchQuery }));
    }, 400);

    return () => clearTimeout(delay);
  }, [dispatch, searchQuery]);

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      setForm((prev) => ({ ...prev, [name]: files[0] }));
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const temp = {};
    if (!form.title) temp.title = "Title is required";
    if (!form.image && !editId) temp.image = "Image is required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const resetForm = () => {
    setForm({
      title: "",
      image: null,
      CTA_text: "",
      CTA_link: "",
      order: 1,
      status: true,
    });
    setErrors({});
    setEditId(null);
    setIsEditing(false);
  };
  const handleStatusToggle = (item) => {
    const data = new FormData();
    data.append("status", !item.status);

    dispatch(updateBanner({ id: item.id, data }))
      .unwrap()
      .then(() => {
        dispatch(getBanners());
        CommonToast(
          `Banner ${!item.status ? "activated" : "deactivated"} successfully`,
          "success",
        );
      })
      .catch(() => CommonToast("Failed to update status", "error"));
  };

  const handleView = (banner) => {
    setViewBanner(banner);
    setIsViewing(true);
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const data = new FormData();
    data.append("title", form.title);
    if (form.image) data.append("image", form.image);
    data.append("CTA_text", form.CTA_text);
    data.append("CTA_link", form.CTA_link);
    data.append("order", form.order);
    data.append("status", form.status);

    const action = editId
      ? updateBanner({ id: editId, data })
      : createBanner(data);

    dispatch(action)
      .unwrap()
      .then(() => {
        dispatch(getBanners());
        resetForm();
        CommonToast(
          editId
            ? "Banner updated successfully"
            : "Banner created successfully",
          "success",
        );
      })
      .catch(() =>
        CommonToast(
          editId ? "Failed to update banner" : "Failed to create banner",
          "error",
        ),
      );
  };

  const handleEdit = (banner) => {
    setEditId(banner.id);
    setForm({
      title: banner.title || "",
      image: null,
      CTA_text: banner.CTA_text || "",
      CTA_link: banner.CTA_link || "",
      order: banner.order || 1,
      status: banner.status ?? true,
    });
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;

    dispatch(deleteBanner(id))
      .unwrap()
      .then(() => {
        dispatch(getBanners());
        CommonToast("Banner deleted successfully", "success");
      })
      .catch(() => CommonToast("Failed to delete banner", "error"));
  };

  const handleAddBanner = () => {
    resetForm();
    setIsEditing(true);
  };
  const paginatedData = banners.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  // if (loading) return <Loader text="Loading banners..." fullScreen={true} />;

  if (isEditing) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Box width="100%">
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1} mb={3}>
            <IconButton onClick={resetForm}>
              <ArrowBackIcon />
            </IconButton>
            <CommonLabel>
              {editId ? "Edit Banner" : "Create Banner"}
            </CommonLabel>
          </Stack>

          {/* Form */}
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                fullWidth
              />

              <Button variant="outlined" component="label">
                <CommonLabel>
                  {editId ? "Change Image" : "Upload Image"}
                </CommonLabel>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  hidden
                  onChange={handleChange}
                />
              </Button>

              {errors.image && (
                <Typography color="error">{errors.image}</Typography>
              )}

              {form.image && (
                <Typography variant="body2">
                  Selected: {form.image.name}
                </Typography>
              )}

              <TextField
                label="CTA Text"
                name="CTA_text"
                value={form.CTA_text}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="CTA Link"
                name="CTA_link"
                value={form.CTA_link}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Order"
                type="number"
                name="order"
                value={form.order}
                onChange={handleChange}
                fullWidth
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.status}
                    name="status"
                    onChange={handleChange}
                  />
                }
                label="Active"
              />

              {/* <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={resetForm}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit}>
                  Save
                </Button>
              </Stack> */}
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <CommonButton variant="outlined">Cancel</CommonButton>
                <CommonButton
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={updateLoading || updateLoading}
                >
                  {updateLoading || updateLoading ? "Saving..." : "Save"}
                </CommonButton>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </Box>
    );
  }
  if (isViewing && viewBanner) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Box width="100%">
          <Stack direction="row" alignItems="center" spacing={1} mb={3}>
            <IconButton
              onClick={() => {
                setIsViewing(false);
                setViewBanner(null);
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <CommonLabel>View Banner</CommonLabel>
          </Stack>

          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="Title"
                value={viewBanner.title}
                fullWidth
                InputProps={{ readOnly: true }}
              />

              {viewBanner.image && (
                <Box>
                  <Typography variant="subtitle2">Banner Image</Typography>
                  <Avatar
                    src={`https://apidata.hogonnindia.com/${viewBanner.image}`}
                    variant="rounded"
                    sx={{ width: 200, height: 100, mt: 1 }}
                  />
                </Box>
              )}

              <TextField
                label="CTA Text"
                value={viewBanner.CTA_text}
                fullWidth
                InputProps={{ readOnly: true }}
              />

              <TextField
                label="CTA Link"
                value={viewBanner.CTA_link}
                fullWidth
                InputProps={{ readOnly: true }}
              />

              <TextField
                label="Order"
                value={viewBanner.order}
                fullWidth
                InputProps={{ readOnly: true }}
              />

              <FormControlLabel
                control={<Checkbox checked={viewBanner.status} disabled />}
                label="Active"
              />
            </Stack>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color: "#7E7E7E", mb: 2 }}
        >
          Banners
        </Typography>
        <CommonButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddBanner()}
        >
          Add Banner
        </CommonButton>
      </Stack>

      <TableContainer component={Paper}>
        <Box sx={{ display: "flex" }}>
          <CommonSearchField
            value={searchQuery}
            placeholder="Search by banner name.."
            onChange={(value) => setSearchQuery(value)}
          />
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Sr</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>CTA Text</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" mt={1}>
                    Loading banners...
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              paginatedData.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                  {/* <TableCell>
                  <Avatar
                    // src={item.image}

                    src={`https://apidata.hogonnindia.com/${item?.image}`}
                    variant="rounded"
                    sx={{ width: 80, height: 45 }}
                  />
                </TableCell> */}
                  <TableCell>
                    <Box sx={{ position: "relative", width: 48, height: 48 }}>
                      {!imageLoaded && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                        </Box>
                      )}

                      <Avatar
                        src={`https://apidata.hogonnindia.com/${item?.image}`}
                        variant="rounded"
                        sx={{ width: 48, height: 48 }}
                        imgProps={{
                          onLoad: () => setImageLoaded(true),
                          onError: () => setImageLoaded(true),
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.CTA_text}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Switch
                        checked={item.status}
                        onChange={() => handleStatusToggle(item)}
                        color="success"
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "success.main",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "success.light",
                            },
                        }}
                      />
                      <Typography
                        variant="body2"
                        color={item.status ? "success.main" : "text.secondary"}
                      >
                        {item.status ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {/* <IconButton color="primary">
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton color="warning" onClick={() => handleEdit(item)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <DeleteIcon />
                  </IconButton> */}
                    <IconButton
                      color="primary"
                      onClick={() => handleView(item)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      color="warning"
                      onClick={() => handleEdit(item)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

            {banners.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No banners found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack alignItems="flex-end" mt={3}>
        <Pagination
          count={Math.ceil(banners.length / rowsPerPage)}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </Stack>
    </Box>
  );
};

export default Banner;
